// @ts-strict-ignore
import { useQueries, useQuery } from '@tanstack/react-query';
import type { WatchlistResponse } from '../types';
import apiCalls from '~/helpers/apiCalls';
import { initialWatchlistData } from '../constants';
import { normalizeMatches } from '../helpers';
import { useMemo } from 'react';

const { watchlists: { getMatches, postAIWatchlistResolver } } = apiCalls;

type GetMatches = {
  evaluations: Array<{
    evaluationToken: string;
    entityToken: string;
  }>;
  showLatestDecisions: boolean;
  typhonDomain: string;
};

export const useGetWatchlistMatches = ({
  evaluations,
  typhonDomain,
  showLatestDecisions = false,
}: GetMatches) => useQueries({
  queries: evaluations.map(({ entityToken, evaluationToken }) => ({
    queryKey: ['watchlist-review', entityToken, evaluationToken, showLatestDecisions, typhonDomain],
    initialData: initialWatchlistData,
    initialDataUpdatedAt: 0,
    staleTime: 30_000,
    enabled: Boolean(entityToken && evaluationToken && typhonDomain),
    queryFn: async (): Promise<WatchlistResponse> => {
      const watchlistResponse = await getMatches({
        canResolve: showLatestDecisions,
        entityToken,
        evaluationToken,
        typhonDomain,
      });

      const formattedMatches = normalizeMatches(watchlistResponse);
      return {
        ...watchlistResponse,
        evaluationToken,
        matches: formattedMatches,
      };
    },
  })),
});


export const useGetAIWatchlistResolver = ({
  evaluationToken,
  typhonDomain,
  enabled = true,
}: {
  evaluationToken: string;
  typhonDomain: string;
  enabled?: boolean;
}) => useQuery({
  queryKey: ['ai-watchlist-resolver', evaluationToken],
  enabled: Boolean(enabled && evaluationToken && typhonDomain),
  queryFn: async () => {
    const response = await postAIWatchlistResolver({
      typhonDomain,
      evaluationToken,
      regenerate: false,
    });
    return response;
  },
});
export const useGetWatchlistResolutionState = watchlistQueriesArray => useMemo(() => (
  watchlistQueriesArray.reduce((acc, { data: { matches, evaluationToken }, fetchStatus }) => {
    if (fetchStatus !== 'idle') acc.isLoadingWatchlistMatches = true; // wait for watchlists to load before allowing review
    matches.forEach(
      ({ is_resolvable: isResolvable, resolution }) => {
        if (isResolvable && !resolution.timestamp) {
          acc.unresolvedEvaluations[evaluationToken] = true;
          acc.hasUnresolvedWatchlistMatches = true;
        }
      },
    );

    return acc;
  }, {
    hasUnresolvedWatchlistMatches: false,
    isLoadingWatchlistMatches: false,
    unresolvedEvaluations: {},
  })
), [watchlistQueriesArray]);
